import { useId, useState, type ReactNode } from 'react'
import { Check, ChevronDown } from 'lucide-react'

/* The parts the filter panel is assembled from: a titled group and a
   checkbox — the two shapes four filters need.

   They are deliberately small and dumb — every one of them takes its value
   and hands back the next one, so the panel above stays a description of the
   filters rather than a pile of markup. */

/* ---------- group ---------- */

type FilterGroupProps = {
  title: string
  children?: ReactNode
  /** Renders the group as a disclosure, closed until it is opened. */
  collapsible?: boolean
  defaultOpen?: boolean
  /** What is chosen inside, shown on the header while it is closed. */
  summary?: string
}

/** One titled block of the panel, ruled off from the one above it. */
export function FilterGroup({
  title,
  children,
  collapsible = false,
  defaultOpen = false,
  summary,
}: FilterGroupProps) {
  const [open, setOpen] = useState(!collapsible || defaultOpen)
  const id = useId()

  const heading = (
    <span className="label-sm text-dim">{title}</span>
  )

  return (
    <div className="border-t border-line-soft px-5 py-5 first:border-t-0">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={id}
          className="flex w-full items-center justify-between gap-3 text-left transition-colors hover:[&>span]:text-lime-deep"
        >
          {heading}
          <span className="flex items-center gap-2">
            {summary && !open ? (
              <span className="max-w-[10rem] truncate text-[12px] text-lime-deep">
                {summary}
              </span>
            ) : null}
            <ChevronDown
              aria-hidden="true"
              strokeWidth={1.8}
              className={`size-4 shrink-0 text-steel transition-transform duration-200 ${
                open ? 'rotate-180' : ''
              }`}
            />
          </span>
        </button>
      ) : (
        <div className="flex min-h-6 items-center">{heading}</div>
      )}

      {open && children ? (
        <div id={collapsible ? id : undefined} className="mt-4">
          {children}
        </div>
      ) : null}
    </div>
  )
}

/* ---------- checkbox ---------- */

type CheckOptionProps = {
  label: string
  checked: boolean
  onChange: () => void
  /** A paint swatch ahead of the label, for the colour group. */
  swatch?: string
  /** How many cars carry it — the count that keeps a filter honest. */
  count?: number
}

/** One tick box and its label. The native input stays, hidden, so the label,
    the keyboard and the screen reader all behave as they should. */
export function CheckOption({
  label,
  checked,
  onChange,
  swatch,
  count,
}: CheckOptionProps) {
  return (
    <label className="group/check flex min-w-0 cursor-pointer items-center gap-2.5 py-1.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className="grid size-4.5 shrink-0 place-items-center rounded-[5px] border border-line bg-raised transition duration-200 group-hover/check:border-lime/60 peer-checked:border-lime peer-checked:bg-lime peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-lime [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100"
      >
        <Check strokeWidth={3.4} className="size-3 text-void transition-opacity" />
      </span>

      {swatch ? (
        <span
          aria-hidden="true"
          className="size-3.5 shrink-0 rounded-full ring-1 ring-white/15"
          style={{ backgroundColor: swatch }}
        />
      ) : null}

      <span className="min-w-0 flex-1 truncate text-[13px] text-silver transition-colors group-hover/check:text-platinum">
        {label}
      </span>

      {count !== undefined ? (
        <span className="shrink-0 text-[11.5px] text-dim tabular-nums">
          {count}
        </span>
      ) : null}
    </label>
  )
}
